import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import {
  Search, Sparkles, Plus, X, MapPin, Phone, Globe,
  Star, ExternalLink, Loader2, ArrowLeft, Check, Building2,
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';

// ============================================================================
// ADD VENDOR MODAL
// ============================================================================

function AddVendorModal({ result, markets, categories, defaultMarket, defaultCategory, onSave, onClose }) {
  const [form, setForm] = useState({
    name: result.name || '',
    category: defaultCategory || '',
    homeMarket: defaultMarket || '',
    additionalMarkets: [],
    willTravel: false,
    contactName: '',
    phone: result.phone || '',
    email: result.email || '',
    website: result.website || '',
    notes: result.description || '',
    isPreferred: false
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      alert('Name and category are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/vendors', { ...form, status: 'ACTIVE' });
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8 animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add to Network</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
            <Sparkles className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">Pre-filled from AI search. Review and edit before saving.</p>
          </div>

          <div>
            <label className="label">Vendor Name *</label>
            <input value={form.name} onChange={e => handleChange('name', e.target.value)} className="input" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category *</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)} className="input" required>
                <option value="">Select...</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Home Market</label>
              <select value={form.homeMarket} onChange={e => handleChange('homeMarket', e.target.value)} className="input">
                <option value="">None</option>
                {markets.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Phone</label>
              <input value={form.phone} onChange={e => handleChange('phone', e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Email</label>
              <input value={form.email} onChange={e => handleChange('email', e.target.value)} className="input" type="email" />
            </div>
          </div>

          <div>
            <label className="label">Website</label>
            <input value={form.website} onChange={e => handleChange('website', e.target.value)} className="input" />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} className="input" rows={3} />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>
              {saving ? 'Adding...' : 'Add to Network'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// RESULT CARD - With expandable description
// ============================================================================

function ResultCard({ result, onAdd, isAdded }) {
  const [expanded, setExpanded] = useState(false);
  
  // Determine the best URL to use
  const websiteUrl = result.website?.startsWith('http') ? result.website : result.website ? `https://${result.website}` : null;
  const sourceUrl = result.sourceUrl?.startsWith('http') ? result.sourceUrl : result.sourceUrl ? `https://${result.sourceUrl}` : null;
  const googleUrl = result.googleSearchUrl || `https://www.google.com/search?q=${encodeURIComponent(result.name + ' ' + (result.address || ''))}`;

  // Check if description is long enough to warrant expansion
  const descriptionLength = result.description?.length || 0;
  const isLongDescription = descriptionLength > 120; // Roughly 2 lines worth

  return (
    <div className={`border rounded-lg p-4 transition-all ${isAdded ? 'bg-green-50 border-green-200' : 'bg-white hover:shadow-md'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold">{result.name}</h3>
          {result.address && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3 shrink-0" /> {result.address}
            </p>
          )}
        </div>
        {result.rating && (
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
            <span className="font-medium">{result.rating}</span>
          </div>
        )}
      </div>

      {/* Expandable Description */}
      {result.description && (
        <div className="mt-2">
          <p className={`text-sm text-gray-600 ${!expanded && isLongDescription ? 'line-clamp-2' : ''}`}>
            {result.description}
          </p>
          {isLongDescription && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-hub-600 hover:text-hub-700 mt-1 flex items-center gap-1"
            >
              {expanded ? (
                <>Read less <ChevronUp className="w-3 h-3" /></>
              ) : (
                <>Read more <ChevronDown className="w-3 h-3" /></>
              )}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-3 text-sm">
        {result.phone && (
          <a href={`tel:${result.phone}`} className="flex items-center gap-1 text-hub-600 hover:underline">
            <Phone className="w-3 h-3" /> {result.phone}
          </a>
        )}
        {websiteUrl && (
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-hub-600 hover:underline">
            <Globe className="w-3 h-3" /> Website <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Source info - clickable link to where we found them */}
      <div className="flex items-center justify-between mt-2 text-xs">
        {sourceUrl && sourceUrl !== websiteUrl ? (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-hub-600 flex items-center gap-1">
            Source: {result.source || 'Web'} <ExternalLink className="w-3 h-3" />
          </a>
        ) : result.source ? (
          <span className="text-gray-400">Source: {result.source}</span>
        ) : <span />}
        
        {/* Google fallback - always available */}
        <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-hub-600 flex items-center gap-1">
          <Search className="w-3 h-3" /> Google
        </a>
      </div>

      <div className="mt-3 pt-3 border-t">
        {isAdded ? (
          <div className="flex items-center gap-2 text-green-600 text-sm">
            <Check className="w-4 h-4" /> Added to network
          </div>
        ) : (
          <button onClick={() => onAdd(result)} className="btn-primary w-full text-sm">
            <Plus className="w-4 h-4" /> Add to Network
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function VendorDiscovery() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Data from DB
  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Search state
  const [query, setQuery] = useState(searchParams.get('search') || '');
  const [market, setMarket] = useState(searchParams.get('market') || '');
  const [category, setCategory] = useState('');
  
  // Results
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  
  // Added vendors
  const [addedNames, setAddedNames] = useState(new Set());
  
  // Modal
  const [showAddModal, setShowAddModal] = useState(null);

  // Load markets and categories
  useEffect(() => {
    const loadData = async () => {
      try {
        const [marketsData, categoriesData] = await Promise.all([
          api.get('/markets'),
          api.get('/event-categories')
        ]);
        const loadedMarkets = Array.isArray(marketsData) ? marketsData : marketsData.markets || [];
        const loadedCategories = Array.isArray(categoriesData) ? categoriesData : categoriesData.categories || [];
        
        setMarkets(loadedMarkets.filter(m => m.active));
        setCategories(loadedCategories.filter(c => c.active));
        
        // Set default market if provided in URL or use first available
        const urlMarket = searchParams.get('market');
        if (urlMarket && loadedMarkets.some(m => m.name === urlMarket)) {
          setMarket(urlMarket);
        } else if (loadedMarkets.length > 0 && !market) {
          setMarket(loadedMarkets[0].name);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, []);

  // Auto-search if params provided
  useEffect(() => {
    if (!dataLoading && searchParams.get('search') && market) {
      handleSearch();
    }
  }, [dataLoading]);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Enter a search term');
      return;
    }
    if (!market) {
      setError('Select a market');
      return;
    }
    
    setLoading(true);
    setError('');
    setHasSearched(true);
    
    try {
      const res = await api.post('/ai/discover-vendors', {
        query: query.trim(),
        market,
        category: category || undefined
      });
      
      setResults(res.results || []);
      
      if (res.results?.length === 0) {
        setError('No vendors found. Try different search terms.');
      }
    } catch (err) {
      console.error('Discovery failed:', err);
      setError(err.message || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAdd = (result) => {
    setShowAddModal(result);
  };

  const handleVendorAdded = () => {
    setAddedNames(prev => new Set([...prev, showAddModal.name]));
    setShowAddModal(null);
  };

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hub-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/vendors')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-hub-600" /> AI Vendor Discovery
          </h1>
          <p className="text-gray-500">Find new vendors to add to your network</p>
        </div>
      </div>

      {/* Search Form */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="balloon artist, tent rental, DJ services..."
                className="input pl-10"
                autoFocus
              />
            </div>
          </div>
          
          <div>
            <label className="label">Market *</label>
            <select value={market} onChange={e => setMarket(e.target.value)} className="input">
              <option value="">Select market...</option>
              {markets.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          
          <div>
            <label className="label">Category (optional)</label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="input">
              <option value="">Auto-detect</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.label}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Searches the web and Google Places to find vendors in your market
          </p>
          <button onClick={handleSearch} disabled={loading || !market} className="btn-primary">
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Search</>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="card p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-hub-600 mx-auto mb-4" />
          <p className="text-gray-600">Searching for vendors...</p>
          <p className="text-sm text-gray-400 mt-1">AI web search can take 15-30 seconds</p>
        </div>
      ) : results.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-700">Found {results.length} potential vendors</h2>
            <button onClick={handleSearch} className="text-sm text-hub-600 hover:text-hub-700 flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> Search again
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((result, idx) => (
              <ResultCard 
                key={idx} 
                result={result} 
                onAdd={handleAdd}
                isAdded={addedNames.has(result.name)}
              />
            ))}
          </div>
        </div>
      ) : hasSearched && !error ? (
        <div className="card p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold text-gray-700 mb-1">No results found</h3>
          <p className="text-gray-500 text-sm">Try different search terms or another market</p>
        </div>
      ) : !hasSearched ? (
        <div className="card p-12 text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-hub-300" />
          <h3 className="font-semibold text-gray-700 mb-1">Find new vendors</h3>
          <p className="text-gray-500 text-sm">Enter a search term and select a market to discover vendors</p>
        </div>
      ) : null}

      {/* Add Modal */}
      {showAddModal && (
        <AddVendorModal
          result={showAddModal}
          markets={markets}
          categories={categories}
          defaultMarket={market}
          defaultCategory={category}
          onSave={handleVendorAdded}
          onClose={() => setShowAddModal(null)}
        />
      )}
    </div>
  );
}
